import type { ProductV2AvailabilityStatus } from '@brandclub/types';

export interface CatalogV2ReviewSummary {
  averageRating?: number;
  reviewCount?: number;
}

export interface CatalogV2VariationOption {
  name: string;
  value: string;
}

export type CatalogV2VariationNameValues = Record<string, string>;

export const toCatalogV2VariationNameValues = (
  options?: CatalogV2VariationOption[] | null,
): CatalogV2VariationNameValues =>
  Object.fromEntries((options ?? []).map((option) => [option.name, option.value]));

export interface CatalogV2CampaignReward {
  campaignId?: string;
  campaignType?: string;
  campaignStatus?: string;
  rewardAmount?: number;
  rewardPercent?: number;
  rewardAmountType?: 'fixed' | 'percent';
  rewardUnlockStrategy?: string;
}

export interface CatalogV2Offer {
  stacklineSku?: string;
  catalogSku?: string;
  retailerId?: number;
  retailerSku?: string;
  retailerName?: string;
  title?: string;
  retailPrice?: number;
  compareAtPrice?: number;
  retailerProductPageUrl?: string;
  imageUrls?: string[];
  isPrimary?: boolean;
  channelType?: string;
  inStock?: boolean;
  rewardAmount?: number;
}

export interface CatalogV2Variant {
  stacklineSku: string;
  title: string;
  variationNameValues?: CatalogV2VariationNameValues;
  retailPrice?: number;
  compareAtPrice?: number;
  imageUrl?: string;
  imageUrls?: string[];
  retailerProductPageUrl?: string;
  featureBullets?: string[];
  isAvailable?: boolean;
  availabilityStatus?: ProductV2AvailabilityStatus;
  isBrandClubReady?: boolean;
  subCategoryId?: number;
  checkoutEligible?: boolean | null;
  isLinkedToInventory?: boolean | null;
  whereToBuyFallback?: boolean | null;
  /** Foreign offer retailers whose link out (WHERE_TO_BUY) is enabled for this product.
   *  Absent ⇒ no policy resolved; `[]` ⇒ none is link-out enabled. */
  whereToBuyOfferRetailerIds?: number[] | null;
  options?: CatalogV2VariationOption[];
  /** Shopify ProductVariant.position. Merchant order of this combination. */
  variantPosition?: number;
  offers?: CatalogV2Offer[];
}

export interface CatalogV2Product {
  stacklineSku: string;
  parentStacklineSku?: string;
  catalogSku?: string;
  title?: string;
  imageUrl?: string;
  imageUrls?: string[];
  brandId?: number;
  brandName?: string;
  retailPrice?: number;
  compareAtPrice?: number;
  retailerId?: number;
  retailerSku?: string;
  retailerName?: string;
  retailerProductPageUrl?: string;
  isAvailable?: boolean;
  availabilityStatus?: ProductV2AvailabilityStatus;
  checkoutEligible?: boolean | null;
  isLinkedToInventory?: boolean | null;
  whereToBuyFallback?: boolean | null;
  whereToBuyOfferRetailerIds?: number[] | null;
  description?: string;
  featureBullets?: string[];
  rewardAmount?: number;
  reviewSummary?: CatalogV2ReviewSummary;
  variantOptions?: CatalogV2VariationOption[];
  variationNameValues?: CatalogV2VariationNameValues;
  campaignRewards?: CatalogV2CampaignReward[];
  variants?: CatalogV2Variant[];
  offers?: CatalogV2Offer[];
}
